import { useCallback, useEffect, useState } from "react"
import { trackEvent } from "@components/utils/tracking.client"

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
}

export const usePwaInstall = () => {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setInstalled] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
    setInstalled(standalone)

    const handlePrompt = (event: Event) => {
      event.preventDefault()
      setInstallEvent(event as BeforeInstallPromptEvent)
      trackEvent("pwa_install_available")
    }
    const handleInstalled = () => {
      setInstalled(true)
      setInstallEvent(null)
      setShowInstructions(false)
      trackEvent("pwa_installed")
    }

    window.addEventListener("beforeinstallprompt", handlePrompt)
    window.addEventListener("appinstalled", handleInstalled)
    return () => {
      window.removeEventListener("beforeinstallprompt", handlePrompt)
      window.removeEventListener("appinstalled", handleInstalled)
    }
  }, [])

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const canInstall = Boolean(installEvent) || (isIos && !isInstalled)

  const install = useCallback(async () => {
    if (!installEvent) {
      setShowInstructions(true)
      return
    }

    await installEvent.prompt()
    const choice = await installEvent.userChoice
    if (choice.outcome === "accepted") {
      setInstalled(true)
      setInstallEvent(null)
      trackEvent("pwa_install_accepted")
    }
  }, [installEvent])

  return {
    canInstall,
    install,
    isInstalled,
    isIos,
    showInstructions,
    closeInstructions: () => setShowInstructions(false),
  }
}
